/**
 * QuickQuestion — Schnellfrage-Hero am oberen Dashboard.
 *
 * Ein einziges großes Textfeld + ein Knopf. Nimmt jede Beschreibung,
 * legt im Hintergrund einen text-only case an, fährt sofort die Mock Jury
 * und reicht caseId + caseContext + Trial-Result an den Parent durch.
 *
 * Der Parent (Dashboard) hängt damit direkt den B2CAdvisor mit
 * `initialResult` an — der User sieht ohne Umweg seine Empfehlung.
 *
 * Pflichtfelder: nur die Beschreibung (≥ 30 Zeichen für Sinnhaftigkeit).
 * Kein Titel, kein CaseType, kein Upload — alles wird automatisch
 * sinnvoll defaultet (Titel aus erstem Satz, CaseType="other",
 * jurisdiction="DE").
 */
import { useState } from "react";
import { createTextOnlyCase, runTrial, type TrialResult } from "../lib/api";

interface Props {
  onResolved: (caseId: string, caseContext: string, result: TrialResult) => void;
}

const MIN_CHARS = 30;

export default function QuickQuestion({ onResolved }: Props) {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<"idle" | "case" | "jury">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = question.trim();
    if (text.length < MIN_CHARS) return;
    setLoading(true);
    setError(null);
    try {
      setPhase("case");
      const tx = await createTextOnlyCase({
        description: text,
        caseType: "other",
      });
      setPhase("jury");
      const result = await runTrial({
        caseId: tx.caseId,
        caseContext: text,
        jurisdiction: "DE",
      });
      onResolved(tx.caseId, text, result);
      // Frage nicht löschen — der User soll sehen was er gefragt hat
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setPhase("idle");
    }
  };

  return (
    <section className="rounded-2xl border border-court-700/60 bg-gradient-to-br from-court-900/40 via-slate-900/40 to-slate-900/40 p-6 shadow-xl shadow-court-900/30">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <h2 className="text-2xl font-semibold text-slate-50">
          ⚡ Schnell-Einschätzung
        </h2>
        <span className="text-xs text-slate-400">
          ohne Upload · in ~30 Sek.
        </span>
      </div>
      <p className="text-sm text-slate-400 mb-5">
        Beschreibe Deinen Fall in eigenen Worten. Die 50-Juroren-Jury und Dr.
        Hartmann geben Dir sofort eine Einschätzung — ohne Account-Aufwand,
        ohne Dokumenten-Upload.
      </p>

      <form onSubmit={submit} className="space-y-3">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={4}
          placeholder="z. B. Mein Flug LH405 Frankfurt → New York am 12.04. hatte 6 Stunden Verspätung wegen technischem Defekt. Lufthansa weigert sich zu zahlen. Was kann ich tun?"
          className="w-full rounded-xl bg-slate-950/70 border border-slate-700 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-court-500"
        />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-500">
            {question.trim().length}/{MIN_CHARS} Zeichen
            {question.trim().length >= MIN_CHARS && " ✓"}
          </p>
          <button
            type="submit"
            disabled={loading || question.trim().length < MIN_CHARS}
            className="rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-court-700/30 transition"
          >
            {phase === "case"
              ? "Lege Fall an…"
              : phase === "jury"
              ? "Schwarm debattiert…"
              : "🧑‍⚖️ Schnell-Einschätzung holen"}
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-950/50 border border-red-800 px-3 py-2 text-xs text-red-200">
            {error}
          </div>
        )}

        <p className="text-[11px] text-slate-500 leading-relaxed pt-2 border-t border-slate-800">
          Brauchst Du eine tiefere Analyse mit Vertrag/Rechnung als PDF?{" "}
          <span className="text-court-300">Scrolle runter → Step 1.</span>
        </p>
      </form>
    </section>
  );
}
